import React, { ReactElement, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useGetLibraryElearningsQuery } from '@/modules/services/courseLibrary';
import { getQueryString, pushParamsToUrl, sanitizeHTML } from '@/lib/util';
import { CourseLibraryDefaultQueryString } from '@/pages/CourseLibrary/CourseLibraryRoutes';
import {
  RemoveRedEyeOutlinedIcon,
  ListTable,
  MenuIcon,
  Spinner,
  Link,
  Modal,
} from 'elmo-elements';
import { useUserHasCapabilities } from '@/lib/auth/useUserHasCapabilities';
import { CourseLibraryPermissions } from '@/lib/auth/permissions/courseLibraryPermissions.enum';
import { useDispatch } from 'react-redux';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import {
  useGetDownloadCourseResultQuery,
  useStartDownloadCourseMutation,
} from '@/modules/services';
import { CourseLibraryDownloadResponse } from '../../types';
import { CopyIcon } from '@/components/Icons';
import './index.scss';
import { Translations } from '@/lib/translation';

const tableColumns = {
  name: { label: 'Name' },
  category: { label: 'Category' },
  modifiedDate: { label: 'Last Modified' },
};

const sortableColumns = [
  { key: 'name', name: 'Name' },
  { key: 'category_id', name: 'Category' },
  { key: 'modified', name: 'Last Modified' },
];

export const enum ModalContext {
  COPY_COURSE_IN_PROGRESS = 'CopyCourseInProgress',
  LEARN_MORE = 'LearnMore',
}

const CourseLibraryList = (): ReactElement => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const pageSize = 10;
  const currentQueryString = getQueryString(
    location.search,
    CourseLibraryDefaultQueryString,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>('');
  const [modalContext, setModalContext] = useState<ModalContext | null>(null);
  const [skip, setSkip] = useState<boolean>(true);
  const [courseIdDownload, setCourseIdDownload] = useState<string>('');

  const { data: libraryData } =
    useGetLibraryElearningsQuery(currentQueryString);
  const downloadResult = useGetDownloadCourseResultQuery(courseIdDownload, {
    pollingInterval: 2000,
    skip,
  });
  const [downloadCourse, downloadCourseStatus] =
    useStartDownloadCourseMutation();

  const canDownload = useUserHasCapabilities([
    CourseLibraryPermissions.DOWNLOAD_COURSE_LIBRARY,
  ]);

  const handleLearnMoreClick = (info = '') => {
    setIsModalOpen(true);
    setModalContext(ModalContext.LEARN_MORE);
    setModalContent(sanitizeHTML(info) as unknown as string);
  };

  const handleDownloadCourse = (courseId: string) => {
    setCourseIdDownload(courseId);
    downloadCourse(courseId);
  };

  const handleSort = (column: number, direction: string) => {
    pushParamsToUrl(
      {
        sort: sortableColumns[column].key,
        order: direction.toUpperCase(),
        page: 1,
      },
      history,
    );
  };

  useEffect(() => {
    if (downloadResult.isError) {
      setSkip(true);
      setCourseIdDownload('');
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message:
            'An error occurred and we were unable to copy this module. Please try again.',
        },
      });
    }

    // Using isFetching because of caching issue for suceeding publish request w/o refreshing page.
    if (
      !downloadResult.isFetching &&
      downloadResult.isSuccess &&
      downloadResult.data
    ) {
      setSkip(true);
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: `Copy course module complete! Please verify in My Library.`,
        },
      });
      setCourseIdDownload('');
      if (
        isModalOpen &&
        modalContext === ModalContext.COPY_COURSE_IN_PROGRESS
      ) {
        setIsModalOpen(false);
      }
    }
  }, [downloadResult, dispatch, history, isModalOpen, modalContext]);

  useEffect(() => {
    if (downloadCourseStatus.isError) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message:
            'data' in downloadCourseStatus.error
              ? (
                  downloadCourseStatus.error
                    .data as CourseLibraryDownloadResponse
                ).message
              : 'Something went wrong with the copy',
        },
      });
    }
    if (
      !downloadCourseStatus.isLoading &&
      downloadCourseStatus.isSuccess &&
      downloadCourseStatus.data
    ) {
      setModalContext(ModalContext.COPY_COURSE_IN_PROGRESS);
      setIsModalOpen(true);
      setSkip(false);
    }
  }, [downloadCourseStatus, dispatch]);

  if (!libraryData) {
    return <Spinner />;
  }
  return (
    <>
      <Modal
        id="LearnMore"
        type="medium"
        isOpened={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title={
          modalContext === ModalContext.COPY_COURSE_IN_PROGRESS
            ? Translations.courseLibrary.copyCourse.inProgressTitle.default
            : 'About this module'
        }
      >
        {modalContext === ModalContext.COPY_COURSE_IN_PROGRESS ? (
          <div className="publish-in-progress-container">
            <div className="publish-in-progress-container__spinner">
              <Spinner size="xl" />
            </div>
            <p>
              {Translations.courseLibrary.copyCourse.inProgressInfo.default}
            </p>
          </div>
        ) : (
          <>
            {/* eslint-disable-next-line jam3/no-sanitizer-with-danger */}
            <div dangerouslySetInnerHTML={{ __html: modalContent }} />
          </>
        )}
      </Modal>
      <ListTable
        id="CourseLibraryTable"
        ariaLabel="List of Course Library Modules"
        onSort={handleSort}
      >
        <ListTable.Header>
          <ListTable.Column label={tableColumns.name.label} sortable={true} />
          <ListTable.Column
            label={tableColumns.category.label}
            sortable={false}
          />
          <ListTable.Column
            label={tableColumns.modifiedDate.label}
            sortable={false}
          />
        </ListTable.Header>
        <ListTable.Body>
          {libraryData.elearnings.map((elearning: any, index: number) => (
            <ListTable.Tr
              data-testid="list-table-item"
              key={elearning.elearningId}
              icon={
                !canDownload && (
                  // eslint-disable-next-line jsx-a11y/anchor-is-valid
                  <Link
                    url="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLearnMoreClick(elearning.elearningInfo);
                    }}
                  >
                    Learn more
                  </Link>
                )
              }
              actions={
                canDownload && [
                  {
                    icon: <RemoveRedEyeOutlinedIcon />,
                    label: 'Preview',
                    onClick: () => {
                      const windowFeatures = `height=${elearning.windowHeight},width=${elearning.windowWidth}`;
                      window.open(
                        elearning.elearningPath,
                        '_blank',
                        windowFeatures,
                      );
                    },
                  },
                  {
                    icon: <CopyIcon />,
                    label: 'Copy to My Library',
                    onClick: () => {
                      handleDownloadCourse(elearning.elearningId);
                    },
                    withDivider: true,
                  },
                  {
                    icon: <MenuIcon />,
                    label: 'Learn more',
                    onClick: () => {
                      handleLearnMoreClick(elearning.elearningInfo);
                    },
                  },
                ]
              }
            >
              <ListTable.Td>{elearning.elearningName}</ListTable.Td>
              <ListTable.Td>
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(elearning.elearningCategory),
                  }}
                />
              </ListTable.Td>
              <ListTable.Td>{elearning.publishedTime}</ListTable.Td>
            </ListTable.Tr>
          ))}
        </ListTable.Body>
        <ListTable.Pagination
          hideDropdown={true}
          pageSize={pageSize}
          currentPage={currentQueryString?.page || 1}
          onPageChange={(pageNumber) => {
            /* This is intentional */
            pushParamsToUrl({ page: pageNumber }, history);
          }}
          onPageSizeChange={() => {}}
          totalResults={libraryData?.count}
        />
      </ListTable>
    </>
  );
};

export default CourseLibraryList;
